import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const ICON_OPTIONS = [
    { key: "wifi", label: "Wi-Fi / Internet" },
    { key: "snowflake", label: "Air Conditioning" },
    { key: "zap", label: "Power / Backup" },
    { key: "building", label: "Building / Infrastructure" },
    { key: "plug", label: "Electricity" },
    { key: "coffee", label: "Tea / Coffee" },
    { key: "sparkles", label: "Cleaning / Shine" },
    { key: "car", label: "Parking" },
    { key: "droplets", label: "Washroom / Water" },
];

const EMPTY = { title: "", desc: "", icon_key: "sparkles", featured: false };

const field =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50";
const labelCls = "text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45";

function AmenityCard({ amenity, onSave, onDelete, isNew }) {
    const [form, setForm] = useState(amenity);
    const [busy, setBusy] = useState(false);
    const dirty = JSON.stringify(form) !== JSON.stringify(amenity);

    const save = async () => {
        if (!form.title.trim()) return toast.error("Title is required");
        setBusy(true);
        try {
            await onSave(form);
            toast.success(isNew ? "Amenity added" : "Amenity updated");
        } catch (e) {
            toast.error(apiError(e, "Failed to save amenity"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={`amenity-card-${amenity.id || "new"}`} className="rounded-2xl border border-slate-200/80 bg-white p-5">
            <div className="space-y-3.5">
                <div>
                    <label className={labelCls}>Title</label>
                    <input data-testid={`amenity-title-${amenity.id || "new"}`} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={field} placeholder="e.g. High-Speed Internet" />
                </div>
                <div>
                    <label className={labelCls}>One-line Description</label>
                    <textarea
                        data-testid={`amenity-desc-${amenity.id || "new"}`}
                        value={form.desc}
                        onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))}
                        rows={2}
                        className={`${field} resize-none`}
                        placeholder="Short description shown under the title"
                    />
                </div>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                    <div>
                        <label className={labelCls}>Icon</label>
                        <select
                            data-testid={`amenity-icon-${amenity.id || "new"}`}
                            value={form.icon_key}
                            onChange={(e) => setForm((f) => ({ ...f, icon_key: e.target.value }))}
                            className={`${field} cursor-pointer`}
                        >
                            {ICON_OPTIONS.map((o) => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end pb-1">
                        <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ink/70">
                            <input
                                data-testid={`amenity-featured-${amenity.id || "new"}`}
                                type="checkbox"
                                checked={form.featured}
                                onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                                className="h-4 w-4 accent-[#2B5CAB]"
                            />
                            Featured (large card)
                        </label>
                    </div>
                </div>
                <div className="flex items-center gap-2.5 border-t border-slate-100 pt-3.5">
                    <button
                        data-testid={`amenity-save-${amenity.id || "new"}`}
                        onClick={save}
                        disabled={busy || (!dirty && !isNew)}
                        className="btn-gradient rounded-full px-5 py-2 text-xs font-bold text-white disabled:opacity-50"
                    >
                        {busy ? "Saving…" : isNew ? "Add Amenity" : "Save"}
                    </button>
                    {onDelete && (
                        <button
                            data-testid={`amenity-delete-${amenity.id}`}
                            onClick={onDelete}
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-ink/60 transition-colors hover:border-red-300 hover:text-red-600"
                        >
                            <Trash2 size={13} strokeWidth={2.5} />
                            Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AmenitiesTab() {
    const [amenities, setAmenities] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/amenities");
            setAmenities(data.amenities);
        } catch (e) {
            toast.error(apiError(e, "Failed to load amenities"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const saveAmenity = async (form, existing) => {
        const payload = { title: form.title, desc: form.desc, icon_key: form.icon_key, featured: form.featured };
        if (existing) {
            await api.put(`/api/admin/amenities/${existing.id}`, payload);
        } else {
            await api.post("/api/admin/amenities", payload);
            setAdding(false);
        }
        await load();
    };

    const deleteAmenity = async (amenity) => {
        if (!window.confirm(`Delete "${amenity.title}"?`)) return;
        try {
            await api.delete(`/api/admin/amenities/${amenity.id}`);
            toast.success("Amenity deleted");
            await load();
        } catch (e) {
            toast.error(apiError(e, "Failed to delete amenity"));
        }
    };

    if (!amenities) {
        return (
            <div className="flex justify-center py-20" data-testid="amenities-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    return (
        <div data-testid="amenities-tab">
            <div className="mb-5 flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-ink/55">Featured amenities show as large cards on the website. Changes go live immediately.</p>
                {!adding && (
                    <button
                        data-testid="add-amenity-btn"
                        onClick={() => setAdding(true)}
                        className="btn-gradient inline-flex shrink-0 items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-bold text-white"
                    >
                        <Plus size={14} strokeWidth={2.6} />
                        Add Amenity
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {adding && <AmenityCard amenity={{ ...EMPTY }} isNew onSave={(form) => saveAmenity(form, null)} onDelete={() => setAdding(false)} />}
                {amenities.map((a) => (
                    <AmenityCard key={a.id} amenity={a} onSave={(form) => saveAmenity(form, a)} onDelete={() => deleteAmenity(a)} />
                ))}
            </div>
        </div>
    );
}
